/**
 * Category Cockpit — the Help drawer: Concepts (the 23 concepts of the cockpit and the checklist
 * of what was asked), Glossary (129 terms in two sides: what it is for · how it is generated
 * today, with the six generation methods and the field × method table), Benchmark (the systems
 * the cockpit borrows from) and Principles. Content ported verbatim from the prototype
 * (kg-cockpit-poc, glossary.js + gen-fragments + concepts), data in help-data.json.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import data from './help-data.json';

export type HelpTab = 'concepts' | 'glossary' | 'bench' | 'principles';

interface Writer { f: string; l: string; n?: string }
interface Gen { m?: string[]; inputs?: string; today?: string; rule?: string; writers?: Writer[]; readers?: Array<Writer | string>; db?: string; empty?: string }
interface GlossaryEntry { t: string; w: string; y: string; h: string }
interface Method { c: string; t: string; d: string }
interface Concept { n: number; t: string; p: string; where: Array<[string, string]>; real: string; asked: string }

export const HELP = data as unknown as {
  sha: string;
  methods: Record<string, Method>;
  gen: Record<string, Gen>;
  glossary: Record<string, GlossaryEntry>;
  aliases: Record<string, string[]>;
  readBy: Record<string, string[]>;
  concepts: Concept[];
  checklist: Array<[string, string, string, string]>;
};

export const HELP_TABS: Array<[HelpTab, string]> = [['concepts', 'Concepts'], ['glossary', 'Glossary'], ['bench', 'Benchmark'], ['principles', 'Principles']];

/** who reads a field, from the generation sweep (gen readers) or the readers fragment */
export function readersOf(key: string): string[] {
  const g = HELP.gen[key];
  if (g && g.readers && g.readers.length) return g.readers.map((r) => (typeof r === 'string' ? r : r.n || `${r.f}:${r.l}`));
  return HELP.readBy[key] || [];
}

export const shortPath = (f: string): string => f.replace(/^backend-merged\//, '').replace(/^services\//, '').replace(/^routes\//, 'routes/');

export function GenChip({ m }: { m: string }) {
  const d = HELP.methods[m];
  if (!d) return null;
  return <span className={`pmk-gen ${d.c}`} data-tip={d.d}>{d.t}</span>;
}

export function Cite({ w }: { w: Writer }) {
  return <code className="pmk-cite" data-tip={`${w.f}:${w.l} @ ${HELP.sha}${w.n ? ` — ${w.n}` : ''}`}>{shortPath(w.f)}:{w.l}</code>;
}

/** a prototype deep link (#book, #decisions, #aspect/<path>/<aspect>…) → the admin route */
export function mapProtoLink(href: string, lastPath: string | null): { to?: string; tab?: HelpTab; label?: string } {
  const cat = (ch?: string) => (lastPath ? `/category-cockpit/category?path=${encodeURIComponent(lastPath)}${ch ? `&ch=${ch}` : ''}` : '/category-cockpit');
  const m = /^#([a-z]+)(?:\/([^/]+))?(?:\/([^/]+))?/.exec(href);
  if (!m) return {};
  const [, v, a] = m;
  switch (v) {
    case 'triage': return { to: '/category-cockpit' };
    case 'book': return { to: a ? `/category-cockpit/category?path=${a}` : cat() };
    case 'aspect': case 'qa': case 'research': case 'edit': return { to: a ? `/category-cockpit/category?path=${a}&ch=aspects` : cat('aspects') };
    case 'decisions': return { to: '/category-cockpit/decide' };
    case 'versions': return { to: '/category-cockpit/journal' };
    case 'flow': return { to: cat('aspects'), label: 'no flow view yet — the aspects chapter is the nearest thing' };
    case 'concepts': return { tab: 'concepts' };
    case 'glossary': return { tab: 'glossary' };
    case 'bench': return { tab: 'bench' };
    default: return {};
  }
}

const BENCH: Array<[string, string, string, string]> = [
  ['Wikidata / Wikibase item page', 'A statement is value + qualifiers + references; ranks; full revision history with diff and undo; constraint-violation reports per property; talk pages', 'provenance and date on every value · version diff · constraint reports = our catalog contrast', 'Schema chapter (source column), Journal, Catalog contrast'],
  ['Palantir Foundry — Ontology Manager', 'Object types, properties, link types; actions with server-side validation; proposals / branches before publish', 'draft-then-publish · actions are the only write path · the backend re-checks the gate', 'every action names its endpoint (Sources)'],
  ['PoolParty / TopBraid EDG (taxonomy governance)', 'Per-concept status draft → approved; quality checks; change requests; SKOS relations', 'status per entry · change request = our draft · relations vocabulary', 'Knowledge notes status, Relations chapter'],
  ['Guru (knowledge cards)', 'Verification cadence ("verify every 90 days"), an owner per card, a trust indicator', 'stale_after as a first-class UI concept · an owner per category', 'Five-state dots; owner = open question'],
  ['Google Merchant / Amazon browse-node attribute guides', 'Per node: required vs optional attributes, valid values, examples', 'crucial vs optional split of aspects and attributes ("without them we cannot generate")', 'is_ready gates vs the rest'],
  ['Wikipedia "citation needed"', 'A visible flag when a claim has no source', 'flag for model-only values (brand tiers from LLM memory)', 'Market data chapter'],
  ['Neo4j Bloom / Aura Workspace', 'Neighbourhood exploration, saved perspectives', 'the relations view, later', 'Relations chapter'],
  ['Notion / Airtable relations and rollups', 'Familiar idioms for relations and derived counts', 'UI vocabulary for identity, demand, relations', 'Identity chapter'],
  ['Git / GitHub pull requests', 'Branch, diff, checks, review, merge, revert — one mental model everyone here already has', 'draft = branch · tests = checks · approve = merge · rollback = revert', 'the draft → test → approve flow (next bands)'],
];

const PRINCIPLES: Array<[string, string]> = [
  ['No fourth readiness model', 'Tier, L1/L3/L5 and is_ready come from category_readiness_scores; the cockpit explains them, never recomputes (2026-07-11).'],
  ['Derive state, store decisions', 'Every column is a view over existing tables. New tables only for drafts, versions, tests, verdicts (2026-07-25).'],
  ['Snapshot from day one', 'Nightly snapshot of the derived state, so trends exist when we need them.'],
  ['Empty ≠ not researched', 'Four states per aspect: fresh · stale · researched-empty · never (+ error). One null today.'],
  ['Human picks, LLM assists and judges', 'Nothing reaches live knowledge without an approve; nothing regenerates customer pages on its own.'],
];

const QUESTIONS = [
  'Row unit = (sector_id, category_path), site as drill-down — confirm.',
  'New "Knowledge Graph" section, or evolve /admin/category-research chapter by chapter?',
  'One open draft per category (branch model) — or per user?',
  'Version = whole-category snapshot, diff computed — or per aspect?',
  'Test defaults: 3–5 products, which section, cost cap per test.',
  'Judge: reuse quality-audit\'s rubric, or a KG rubric? Owner?',
  'Layer order: data we have (demand, contrast, intent) before new sources (news, market)?',
  'Spec-cache scope (per site today; 12.5 % duplicated paths = 35.7 % of that layer\'s spend) — shown here, decided where?',
  'Reserve facet on documents and knowledge entries now, UI later — agree?',
  'Access: super-admin only in v1; a "KG worker" role later?',
];

export function Principles() {
  return (
    <div className="pmk-principles">
      {PRINCIPLES.map(([t, p]) => <div key={t}><b>{t}</b>{p}</div>)}
    </div>
  );
}

function GlossaryCard({ k, e }: { k: string; e: GlossaryEntry }) {
  const g = HELP.gen[k];
  const rd = readersOf(k);
  const al = HELP.aliases[k] || [];
  return (
    <div className="pmk-gcard">
      <div className="pmk-gcard-t">{e.t} {g && (g.m || []).map((m) => <GenChip key={m} m={m} />)}{al.length ? <span className="pmc-small pmc-mute"> also: {al.join(', ')}</span> : null}</div>
      <div className="pmk-up">what it is for</div>
      <dl className="pmk-kv" style={{ gridTemplateColumns: '80px 1fr', margin: '4px 0' }}>
        <dt>what</dt><dd>{e.w}</dd>
        <dt>why here</dt><dd>{e.y}</dd>
        {rd.length ? <><dt>read by</dt><dd>{rd.join(' · ')}</dd></> : null}
      </dl>
      <div className="pmk-up">how it is generated today</div>
      <dl className="pmk-kv" style={{ gridTemplateColumns: '80px 1fr', margin: '4px 0' }}>
        <dt>today</dt><dd>{(g && g.today) || e.h}</dd>
        {g?.rule ? <><dt>rule</dt><dd>{g.rule}</dd></> : null}
        {g?.inputs ? <><dt>inputs</dt><dd>{g.inputs}</dd></> : null}
        {g?.writers?.length ? <><dt>code</dt><dd>{g.writers.map((w, i) => <div key={i}><Cite w={w} /> <span className="pmc-mute">{w.n || ''}</span></div>)}</dd></> : null}
        {g?.db && g.db !== '—' ? <><dt>db says</dt><dd>{g.db}</dd></> : null}
      </dl>
    </div>
  );
}

export function HelpDrawer({ tab, setTab, onClose, lastPath }: { tab: HelpTab; setTab: (t: HelpTab) => void; onClose: () => void; lastPath: string | null }) {
  const [q, setQ] = useState('');
  const entries = useMemo(() => Object.keys(HELP.glossary).sort(), []);
  const fields = useMemo(() => Object.keys(HELP.gen).filter((k) => HELP.glossary[k]), []);
  const shown = useMemo(() => entries.filter((k) => { if (!q) return true; const e = HELP.glossary[k]; const g = HELP.gen[k]; return `${k} ${e.t} ${e.w} ${e.y} ${e.h} ${g?.today || ''} ${(HELP.aliases[k] || []).join(' ')}`.toLowerCase().includes(q.toLowerCase()); }), [entries, q]);

  const link = (href: string, label: string) => {
    const m = mapProtoLink(href, lastPath);
    if (m.tab) return <button key={href} type="button" className="pmc-btn" onClick={() => setTab(m.tab as HelpTab)}>{label} →</button>;
    if (m.to) return <Link key={href} className="pmc-btn" to={m.to} onClick={onClose} title={m.label}>{label} →</Link>;
    return <span key={href} className="pmc-small pmc-mute">{label}</span>;
  };

  return (
    <aside className="pmk-help" role="dialog" aria-label="Help">
      <div className="pmk-help-hd">
        {HELP_TABS.map(([k, l]) => <button key={k} type="button" className={`pmk-tool ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>{l}</button>)}
        <button type="button" className="pmk-tool pmk-help-x" onClick={onClose} aria-label="Close help">Close ✕</button>
      </div>
      <div className="pmk-help-body">
        {tab === 'concepts' && (
          <>
            <p className="pmc-small pmc-mute" style={{ marginTop: 0 }}>The concepts of the cockpit, on one page. Each card names the concept, where it lives (click to open) and what is real. The table below checks the list of Monday 2026-09-14 and the additions of that week; items marked "mocked" in the prototype are the bands not yet built in the admin.</p>
            <div className="pmk-concepts">
              {HELP.concepts.map((c) => (
                <div key={c.n} className="pmk-concept">
                  <h3><span className="n">{String(c.n).padStart(2, '0')}</span>{c.t}</h3>
                  <p>{c.p}</p>
                  <div className="pmk-actions" style={{ marginTop: 6 }}>{c.where.map(([hh, l]) => link(hh, l))}<span className={`pmk-tag ${/mock/.test(c.real) && !/real/.test(c.real) ? 'warn' : 'ok'}`} style={{ marginLeft: 'auto' }}>{c.real}</span></div>
                  <div className="pmk-src">asked: {c.asked}</div>
                </div>
              ))}
            </div>
            <h3 className="pmk-up" style={{ margin: '18px 0 8px' }}>Checklist · what was asked, and where it is</h3>
            <table className="pmk-t">
              <thead><tr><th>#</th><th>Asked</th><th>State in the prototype</th></tr></thead>
              <tbody>{HELP.checklist.map(([n, t, st], i) => <tr key={i}><td className="pmc-small">{n}</td><td>{t}</td><td><span className={`pmk-tag ${/mock/.test(st) ? 'warn' : 'ok'}`}>{st}</span></td></tr>)}</tbody>
            </table>
          </>
        )}
        {tab === 'glossary' && (
          <>
            <p className="pmc-small pmc-mute" style={{ marginTop: 0 }}>Every term of the cockpit in two sides, as asked on 2026-09-16: <b>what it is for</b> (what · why it is in the cockpit · who reads it) and <b>how it is generated today</b> (method · rule · inputs · code · what the database says). Code read at origin/main @ {HELP.sha}.</p>
            <div className="pmk-gcard">
              <div className="pmk-gcard-t">The six ways knowledge gets into the graph</div>
              <div className="pmk-methods">{Object.keys(HELP.methods).map((m) => <div key={m}><GenChip m={m} /><span className="pmc-small">{HELP.methods[m].d}</span></div>)}</div>
              <div className="pmc-small pmc-mute" style={{ marginTop: 8 }}>Jaume, 2026-09-16: "we want to know which fields come from pure LLM, which from grounded search, and which are grounded on the catalogues of our customers — sometimes two or three of them."</div>
            </div>
            <details className="pmk-more" style={{ margin: '10px 0' }}>
              <summary>Field × method table <span className="pmc-mute">{fields.length} fields</span></summary>
              <div className="pmk-tablewrap">
                <table className="pmk-t">
                  <thead><tr><th>field</th><th>methods</th><th>how it is generated today</th><th>db says</th></tr></thead>
                  <tbody>{fields.map((k) => { const g = HELP.gen[k]; return <tr key={k}><td><code>{HELP.glossary[k].t}</code></td><td>{(g.m || []).map((m) => <GenChip key={m} m={m} />)}</td><td className="pmc-small">{g.today || ''}</td><td className="pmc-small pmc-mute">{g.db && g.db !== '—' ? g.db : ''}</td></tr>; })}</tbody>
                </table>
              </div>
            </details>
            <div className="pmk-filters" style={{ padding: '6px 0' }}>
              <input className="pmc-input" type="search" placeholder="Search a term…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the glossary" />
              <span className="pmc-small pmc-mute">{shown.length} of {entries.length} terms</span>
            </div>
            <div className="pmk-gcards">{shown.map((k) => <GlossaryCard key={k} k={k} e={HELP.glossary[k]} />)}</div>
          </>
        )}
        {tab === 'bench' && (
          <>
            <p className="pmc-small pmc-mute" style={{ marginTop: 0 }}>Jaume's method: step back, look at how existing systems let a human maintain knowledge, borrow the visual concepts that make a person feel in control, then add our layers. Each row names the one idea it lends this cockpit.</p>
            <div className="pmk-tablewrap">
              <table className="pmk-t">
                <thead><tr><th>System</th><th>What it does well for a knowledge maintainer</th><th>What we borrow</th><th>Where in the cockpit</th></tr></thead>
                <tbody>{BENCH.map((r) => <tr key={r[0]}><td><b>{r[0]}</b></td><td className="pmc-small">{r[1]}</td><td className="pmc-small">{r[2]}</td><td className="pmc-small">{r[3]}</td></tr>)}</tbody>
              </table>
            </div>
            <div className="pmk-two" style={{ marginTop: 12 }}>
              <div className="pmk-gcard">
                <div className="pmk-gcard-t">Three ideas to keep</div>
                <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <li><b>Every value carries a reference and a date.</b> Wikidata's statement model. In our tables: research_source, research_model, last_researched_at, source on canonical attributes — already there, never shown together.</li>
                  <li><b>Constraint reports, not just presence.</b> "Canonical attribute extracted on 4 % of products" is a violation report, computed from the catalog. That is the GOOD level.</li>
                  <li><b>Branch before publish.</b> Draft → checks → merge → revert. The split proposals table already follows this pattern (dry_run → applied → rolled_back, before/after metrics).</li>
                </ol>
              </div>
              <div className="pmk-gcard">
                <div className="pmk-gcard-t">Questions still open</div>
                <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12 }}>{QUESTIONS.map((qq) => <li key={qq}>{qq}</li>)}</ol>
              </div>
            </div>
          </>
        )}
        {tab === 'principles' && (
          <>
            <p className="pmc-small pmc-mute" style={{ marginTop: 0 }}>Five rules every screen of this cockpit follows. They come from the decisions of 2026-07-11, 2026-07-25 and the daily of 2026-09-14.</p>
            <Principles />
            <h3 className="pmk-up" style={{ margin: '18px 0 8px' }}>What is real, what is derived, what is not built yet</h3>
            <table className="pmk-t">
              <thead><tr><th>Screen</th><th>Real (read from the database)</th><th>Derived in the cockpit</th><th>Not built yet</th></tr></thead>
              <tbody>
                <tr><td>Categories</td><td>categories, aspect states, canonical counts, products / enriched / pages per site, readiness, open gaps</td><td>next action, flags, attention, four dots, queues</td><td>—</td></tr>
                <tr><td>Category</td><td>all 14 chapters' data, the aspects' content, the schema per attribute, the pending gaps, the readiness history, the reviewers' notes, the sector context</td><td>chapter status and ladder, the briefs, the reach of a change</td><td>propose a change, quality assurance, research from here, edit, test, approve, versions, schedulers, uploads</td></tr>
                <tr><td>To decide</td><td>every question and its counts</td><td>the question text, who decides, what it unblocks</td><td>answering from here (the owning page does)</td></tr>
                <tr><td>Journal</td><td>the writes recorded by 7 tables</td><td>one sentence per write, reversible or not</td><td>versions with snapshot, diff, rollback</td></tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </aside>
  );
}
